/**
 * The finish-the-bet layer (owner, 2026-09-01).
 *
 * Confirmed adds were quietly dying between coupon and placement: 39% of adding
 * sessions placed nothing, and on top fixtures most added legs were dropped or
 * the slip abandoned (traced live on Аmed СК and Villa–Arsenal, 2026-09-01).
 * The widget's post-add behaviour was deliberately passive — collapse to the
 * pill, never touch the operator's UI — and stays so; what changes is what OUR
 * pill does while a pick of ours sits unplaced in the coupon:
 *
 *  - the pill stops rotating fixtures and shows the held pick with its LIVE
 *    price (the operator's own coupon factor, so it is never stale), and
 *  - ONE quiet nudge per session fires if the pick is still unplaced after
 *    `nudgeAfterMs` — once, in our own shadow DOM, never a popup over theirs.
 *
 * A placement hands the pill back to the next advice (owner's rule verbatim);
 * removing every held leg does the same, silently — a user who dropped the
 * pick has answered, and nagging them re-litigates it.
 *
 * This class is the STATE only — renderer-agnostic, timer-injectable, so the
 * once-per-session rule and the cancel paths are testable without a DOM.
 */

export interface SlipHold {
  /** The suggestion id of the newest held pick — the renderer opens on it. */
  id?: string;
  /** The held suggestion itself, so the renderer can draw it without relying
   *  on its own memory of the list the add came from. */
  card?: unknown;
  label: string;
  odds: number | null;
  count: number;
}

interface HeldPick {
  id: string;
  label: string;
  odds: number | null;
  stakeIds: number[];
  eventId?: string;
  /** Each leg's own name, so a combo that loses legs is re-labelled: down to
   *  one leg it names THAT leg; with several left it names the new count via
   *  `comboLabel`. Owner's screenshot 2026-09-10: «Экспресс из 4 событий · 1.12»
   *  over a coupon holding one leg. */
  legLabels?: Record<number, string>;
  comboLabel?: (n: number) => string;
  card?: unknown;
}

/** Where "when did this hold start" survives an SDK rebuild. `index.ts` backs it
 *  with sessionStorage; tests drive it directly. read() returns null when nothing
 *  is held. */
export interface HeldSinceStore {
  read: () => number | null;
  write: (ts: number) => void;
  clear: () => void;
}

/** The default: per-instance only, i.e. the pre-2026-09-02 behaviour. A caller
 *  that does not persist gets a countdown that restarts on every rebuild — which
 *  is exactly the defect, so the SDK always passes a real store. */
export function memoryHeldSince(): HeldSinceStore {
  let at: number | null = null;
  return { read: () => at, write: (ts) => { at = ts; }, clear: () => { at = null; } };
}

export class SlipHoldTracker {
  private picks: HeldPick[] = [];
  private nudgeTimer: ReturnType<typeof setTimeout> | null = null;
  private nudged = false;

  constructor(
    private onChange: (hold: SlipHold | null) => void,
    private onNudge: () => void,
    private nudgeAfterMs = 120_000,
    /** Whether this session may still be nudged. ⛔ Pass a FUNCTION: it is read
     *  when the timer FIRES, not when the tracker is built. A discarded
     *  instance's `setTimeout` keeps running after the SPA rebuilds the widget,
     *  so a construction-time boolean let a dead instance nudge a session that
     *  had already been nudged — the "one per session" claim was true of the
     *  flag and not of the user. A plain boolean is still accepted (tests, and
     *  the meaning is unchanged when nothing can rebuild). */
    private nudgeEnabled: boolean | (() => boolean) = true,
    /** ⛔ WHEN the hold started, across SDK REBUILDS. The host is an SPA that
     *  throws our widget away and rebuilds it on every navigation, so a
     *  countdown living only in `nudgeTimer` restarts each time — a user who
     *  navigates more often than every two minutes never reaches the nudge at
     *  all. The one-per-session FLAG was tested and worked; whether the first
     *  nudge ever fires was not, and it mostly did not. Injected rather than
     *  read from sessionStorage here so a test can drive it. */
    private heldSince: HeldSinceStore = memoryHeldSince(),
  ) {}

  active(): boolean {
    return this.picks.length > 0;
  }

  hold(pick: HeldPick): void {
    this.picks = this.picks.filter((p) => p.id !== pick.id);
    this.picks.push(pick);
    this.armNudge();
    this.emit();
  }

  /** A leg left the coupon (user removal — the bus watcher already filtered
   *  out placements, sweeps and dead legs). The pick survives until its LAST
   *  leg is gone. */
  removeStake(stakeId: number): void {
    const before = this.picks.length;
    let relabelled = false;
    this.picks = this.picks
      .map((p) => {
        if (!p.stakeIds.includes(stakeId)) return p;
        const stakeIds = p.stakeIds.filter((id) => id !== stakeId);
        let label = p.label;
        if (stakeIds.length === 1 && p.legLabels && p.legLabels[stakeIds[0]]) {
          label = p.legLabels[stakeIds[0]];
        } else if (stakeIds.length > 1 && p.comboLabel) {
          label = p.comboLabel(stakeIds.length);
        }
        if (label !== p.label) relabelled = true;
        return { ...p, stakeIds, label };
      })
      .filter((p) => p.stakeIds.length > 0);
    if (this.picks.length === 0) this.cancelNudge();
    if (this.picks.length !== before || this.picks.length === 0 || relabelled) this.emit();
  }

  /** The pick's event has ended or been pulled: it is on neither the live pool
   *  nor the pre-match board. Owner's screenshot 2026-09-09: "В ВАШЕМ БИЛЕТЕ
   *  Тиафо · 1.98" on a page saying the event was over. Picks without an
   *  eventId (older callers, tests) are never expired here. */
  expireGone(isKnown: (eventId: string) => boolean): void {
    const before = this.picks.length;
    this.picks = this.picks.filter((p) => !p.eventId || isKnown(p.eventId));
    if (this.picks.length === before) return;
    if (this.picks.length === 0) this.cancelNudge();
    this.emit();
  }

  /** A bet was placed: the coupon did its job. Clear everything — the pill
   *  goes back to the next advice, and the nudge dies unfired. */
  placed(): void {
    this.picks = [];
    this.cancelNudge();
    this.emit();
  }

  /** Re-read the held legs' prices. `oddsOf` answers with the operator's own
   *  coupon factor; null (unreadable) keeps the last known price rather than
   *  blanking a number the user just saw. */
  refresh(
    oddsOf: (stakeId: number) => number | null,
    /** The coupon's OWN total when the operator shows one («Коэф.: 3.2»). Wins
     *  over re-multiplying, because it is the number the user is looking at.
     *  Only consulted for a combo whose legs are the whole coupon. */
    couponTotal: (legs: number) => number | null = () => null,
  ): void {
    let moved = false;
    for (const p of this.picks) {
      // A single's price is its one leg. A combo is re-multiplied from its legs'
      // live coupon factors when EVERY leg answers — a pill saying 2.73 beside a
      // coupon saying 3.2 (owner's screenshot, 2026-09-09) is a wrong number, not
      // a modest one. A leg that cannot be read keeps the last known price.
      let fresh: number | null = null;
      if (p.stakeIds.length === 1) {
        fresh = oddsOf(p.stakeIds[0]);
      } else {
        fresh = this.picks.length === 1 ? couponTotal(p.stakeIds.length) : null;
        if (fresh == null) {
          let product = 1;
          let complete = true;
          for (const id of p.stakeIds) {
            const f = oddsOf(id);
            if (f == null || !(f > 0)) { complete = false; break; }
            product *= f;
          }
          fresh = complete ? Math.round(product * 100) / 100 : null;
        }
      }
      if (fresh != null && fresh !== p.odds) {
        p.odds = fresh;
        moved = true;
      }
    }
    if (moved) this.emit();
  }

  private emit(): void {
    if (this.picks.length === 0) {
      this.onChange(null);
      return;
    }
    const newest = this.picks[this.picks.length - 1];
    this.onChange({ id: newest.id, card: newest.card, label: newest.label, odds: newest.odds, count: this.picks.length });
  }

  /** Read at fire time — see the constructor. */
  private mayNudge(): boolean {
    return typeof this.nudgeEnabled === "function" ? this.nudgeEnabled() : this.nudgeEnabled;
  }

  private armNudge(): void {
    if (!this.mayNudge() || this.nudged || this.nudgeTimer) return;
    // The clock started when the pick was FIRST held, which may have been
    // several rebuilds ago. Arm for what is left of it, not for the full wait.
    const now = Date.now();
    let started = this.heldSince.read();
    if (started == null || started > now) {
      started = now;
      this.heldSince.write(now);
    }
    const remaining = Math.max(0, this.nudgeAfterMs - (now - started));
    this.nudgeTimer = setTimeout(() => {
      this.nudgeTimer = null;
      if (this.picks.length === 0 || this.nudged || !this.mayNudge()) return;
      this.nudged = true;
      this.onNudge();
    }, remaining);
  }

  private cancelNudge(): void {
    if (this.nudgeTimer) {
      clearTimeout(this.nudgeTimer);
      this.nudgeTimer = null;
    }
    // Nothing is held any more, so the next hold starts a fresh two minutes.
    // Without this the stored timestamp would outlive the pick and the NEXT
    // hold would inherit an already-expired clock and nudge instantly.
    this.heldSince.clear();
  }
}

/** The expiry tick's own gate: an empty board is "unknown", not "ended". */
export function boardsKnown(liveCount: number, preCount: number): boolean {
  return liveCount > 0 && preCount > 0;
}
